import {
  createContext,
  ReactNode,
  useContext,
  useSyncExternalStore
} from "react";
import { useSyncExternalStoreWithSelector } from "use-sync-external-store/shim/with-selector";
import {
  Dispatch,
  isReduxStore,
  ReduxStore,
  State,
  StateStoreLike,
  StateStore,
  ValueUpdater
} from "@zyzgroup/core-common";

// Here’s a few alternatives you should consider before using context:
// 1. Start by passing props
// 2. Extract components and pass JSX as children to them
// Use cases for context
// 1. Theming
// 2. Current account
// 3. Routing
// 4. Managing state

// type AppState = {
//   user: {
//     name: string;
//     age: number;
//   };
// };
// export const { AppStoreProvider, useAppState, useSetAppState, useDispatch } = createAppStoreContext<AppState>(store);
// AppStoreProvider(children);
// const appState = useAppState();
// const user = useAppState((s) => s.user);
// const name = useAppState((s) => s.user.name);
// const dispatch = useDispatch();

export function createAppStoreContext<T extends State>(store: StateStore<T>) {
  const AppStoreContext = createContext<StateStore<T> | null>(null);

  function AppStoreProvider({ children }: { children: ReactNode }) {
    return (
      <AppStoreContext.Provider value={store}>
        {children}
      </AppStoreContext.Provider>
    );
  }

  function useAppStore(): StateStore<T> {
    // 拿到最近一层 Provider
    const store = useContext(AppStoreContext);
    if (!store) {
      throw new ReferenceError(
        "createAppStoreContext's hooks cannot be called outside of AppStoreProvider"
      );
    }
    return store;
  }

  function useAppState(): T;
  function useAppState<Selected>( // Selected is infer type
    selector: (state: T) => Selected,
    equalityFn?: (a: Selected, b: Selected) => boolean
  ): Selected;
  function useAppState<Selected>(
    selector?: (state: T) => Selected,
    equalityFn?: (a: Selected, b: Selected) => boolean
  ) {
    const store = useAppStore();
    const subscribe = (onStoreChange: () => void) =>
      store.subscribe(() => onStoreChange());
    if (selector) {
      // 仅当 selector 结果发生变化时才触发重新渲染
      // eslint-disable-next-line react-hooks/rules-of-hooks
      return useSyncExternalStoreWithSelector(
        subscribe,
        store.getState,
        store.getState,
        selector,
        equalityFn
      );
    }
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useSyncExternalStore(subscribe, store.getState, store.getState);
  }

  function useSetAppState(): ValueUpdater<T> {
    const store = useAppStore();
    if (isReduxStore(store)) {
      throw new Error("useSetAppState is only supported for StateStoreLike");
    }
    return store.setState.bind(store);
  }

  function useDispatch(): Dispatch {
    const store = useAppStore();
    if (!isReduxStore(store)) {
      throw new Error("useDispatch is only supported for ReduxStore");
    }
    return store.dispatch.bind(store);
  }

  return {
    AppStoreProvider,
    useAppStore,
    useAppState,
    useSetAppState,
    useDispatch
  };
}
